// API
import { BriefingApiModel } from '../../../api/briefings/model/briefing-api.model';
import { BriefingBlockTypeApiEnum } from '../../../api/briefings/enum/briefing-block-type-api.enum';
import { BriefingBlockApiModel } from '../../../api/briefings/model/briefing-block-api.model';
import { BriefingTitleDisplayElementApiModel } from '../../../api/briefings/model/briefing-title-display-element-api.model';
import { BriefingTextDisplayElementApiModel } from '../../../api/briefings/model/briefing-text-display-element-api.model';
import { BriefingImageDisplayElementApiModel } from '../../../api/briefings/model/briefing-image-display-element-api.model';
import { BriefingDateDisplayElementApiModel } from '../../../api/briefings/model/briefing-date-display-element-api.model';
import { AlertMentionListDisplayElementApiModel } from '../../../api/briefings/model/alert-mentions-list-display-element-api.model';
import { SearchCriteriaSortFieldEnum } from '../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';

// Page Builder
import { BlockModel } from '../../../Common/PageBuilder/models/block.model';
import { DateDisplayElementModel } from '../../../Common/PageBuilder/models/date-display-element.model';
import { TextDisplayElementModel } from '../../../Common/PageBuilder/models/text-display-element.model';
import { BriefingImageDisplayElementModel } from '../../../Common/PageBuilder/models/briefing-image-display-element.model';
import { BriefingStatusEnum } from '../../../Common/MonitorShared/enum/briefing-status.enum';

// Briefings
import { BriefingModel } from '../../Briefings/models/briefing.model';
import { BriefingTypeEnum } from '../../Briefings/enums/briefing-type.enum';
import {
    BriefingBlockTypeEnum,
    briefingBlockTypeEnumFromApi,
    briefingBlockTypeEnumToApi,
    columnNumberToBriefingBlockTypeMap
} from '../../Briefings/enums/briefing-block-type.enum';
import { BriefingTextDisplayElementModel } from '../../Briefings/models/briefing-text-display-element.model';

// This module
import { AlertMentionListDisplayElementModel } from './alert-mention-list-display-element.model';
import { alertBlockTypeEnumToBlockTypeMap } from './alerts-block-types.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { SavedSearchType } from '../../../Common/MonitorShared/enum/saved-search-type.enum';
import { AlertSurgeDisplayElementApiModel } from './alert-surge-display-element-api.model';
import { AlertSurgeDisplayElementModel } from './alert-surge-display-element.model';
import { AlertTypeEnum } from '../../../Common/MonitorShared/enum/alert-type.enum';
import { MentionSourceApiTypesEnum,
    mentionSourceApiTypesEnumToMonitoringSeriesTypeEnum,
    monitoringSeriesTypeEnumToMentionSourceApiTypesEnum
} from '../../../api/mentions/enum/mention-source-api-types.enum';
import { BriefingTitleDisplayElementModel } from '@Monitor/Briefings/models/briefing-title-display-element.model';
import { CuratorBlock } from '@Common/PageBuilder/models/curator-block.model';
import { BriefingSearchApi } from '@api/briefings/model/briefing-search-api';
import { BriefingCoverageCuratorDisplayElementApiModel } from '@api/briefings/model/briefing-coverage-curator-display-element-api.model';
import { DynamicMentionListDisplayElementModel } from '@Monitor/Briefings/models/dynamic-mention-list-display-element.model';
import { BriefingAutomaticMentionListDisplayElementApiModel } from '@api/briefings/model/briefing-automatic-mention-list-display-element-api.model';
import { AnalysisDisplayElementModel } from '@Monitor/Briefings/models/briefing-analysis-display-element.model';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { BriefingAnalysisDisplayElementApiModel } from '@api/briefings/model/briefing-analysis-display-element-api.model';
import { CuratorDisplayElement } from '@Common/PageBuilder/models/curator-display-element.model';
import { CuratorBlockType } from '@Common/PageBuilder/models/curator-block-type.model';
import { BriefingChartDisplayElementModel } from '@Common/PageBuilder/models/briefing-chart-display-element.model';
import { ChartDisplayElementModel } from '@Common/Reports/models/chart-display-element.model';
import { DividerDisplayElementModel } from '@Common/PageBuilder/models/divider-display-element.model';
import { BriefingDividerDisplayElementApiModel } from '@api/briefings/model/briefing-divider-display-element-api.model';
import { LayoutDisplayElement } from '@Common/PageBuilder/models/layout-display-element.model';
import { LayoutBlock } from '@Common/PageBuilder/models/layout-block.model';
import { LayoutBlockType } from '@Common/PageBuilder/models/layout-block-type.model';
import { BriefingChartDisplayElementApiModel } from '@api/briefings/model/briefing-chart-display-element-api.model';
import { AiAnalysisTypes } from '@Common/Mentions/enums/ai-analysis-types.enum';
import {
    LayoutBlockDisplayRatiosEnum,
    layoutBlockDisplayRatiosToWidthPercentMap
} from '@Common/PageBuilder/enums/layout-block-display-ratios.enum';
/**
 * Alerts are just an extension of briefings, so most things can be pulled directly from briefings
 * This container serves only to override certain parts of briefings to behave how we want for alerts
 */
export class AlertModel extends BriefingModel {
    alertIsSubscriptionSearch: boolean;
    alertIsThemeSearch: boolean;
    alertIsAgentSearch: boolean;
    alertIsAiAgentSearch: boolean;
    filterSearch: MentionSearchModel = new MentionSearchModel();

    constructor(apiModel?: BriefingApiModel) {
        super(apiModel);

        if (apiModel) {
            this.alertIsSubscriptionSearch = false;
            this.alertIsThemeSearch = false;
            this.alertIsAgentSearch = false;
            this.alertIsAiAgentSearch = false;

            if (apiModel.alertSourceType) {
                switch (apiModel.alertSourceType) {
                    case MentionSourceApiTypesEnum.SUBSCRIPTION:
                        this.alertIsSubscriptionSearch = true;
                        break;
                    case MentionSourceApiTypesEnum.THEME:
                        this.alertIsThemeSearch = true;
                        break;
                    case MentionSourceApiTypesEnum.AGENT:
                        this.alertIsAgentSearch = true;
                        break;
                    case MentionSourceApiTypesEnum.AI_AGENT:
                        this.alertIsAiAgentSearch = true;
                        break;
                    default:
                        break;
                }
            }

            this.filterSearch.id = apiModel.filterSearchCriteriaId;
            this.filterSearch.type = SavedSearchType.ALERT;
        }

        if (!apiModel || !this.type) {
            this.type = BriefingTypeEnum.ALERT;
            this.alertIsSubscriptionSearch = false;
            this.alertIsThemeSearch = false;
            this.alertIsAgentSearch = false;
            this.alertIsAiAgentSearch = false;
            this.filterSearch.type = SavedSearchType.ALERT;
        }
    }

    private recalculateLayoutWidth(styles: any, columnCount: number): void {
        if (!styles || columnCount <= 0) {
            return;
        }

        if (styles.width?.length === columnCount) {
            return;
        }

        const defaultDisplayRatio = columnCount === 3
            ? LayoutBlockDisplayRatiosEnum.ONE_TO_ONE_TO_ONE
            : LayoutBlockDisplayRatiosEnum.ONE_TO_ONE;
        let displayRatio = styles.displayRatio || defaultDisplayRatio;
        let width = layoutBlockDisplayRatiosToWidthPercentMap.get(displayRatio);

        if (width?.length !== columnCount) {
            displayRatio = defaultDisplayRatio;
            width = layoutBlockDisplayRatiosToWidthPercentMap.get(displayRatio);
        }

        styles.displayRatio = displayRatio;
        styles.width = width?.length === columnCount
            ? width.slice()
            : Array(columnCount).fill(`${100 / columnCount}%`);
    }

    toApiModel(): BriefingApiModel {
        const model = super.toApiModel();
        model.filterSearchCriteriaId = this.filterSearch.id;
        if (this.alertIsSubscriptionSearch) {
            model.alertSourceType = MentionSourceApiTypesEnum.SUBSCRIPTION;
        } else if (this.alertIsThemeSearch) {
            model.alertSourceType = MentionSourceApiTypesEnum.THEME;
        } else if (this.alertIsAgentSearch) {
            model.alertSourceType = MentionSourceApiTypesEnum.AGENT;
        } else if (this.alertIsAiAgentSearch) {
            model.alertSourceType = MentionSourceApiTypesEnum.AI_AGENT;
        } else {
            model.alertSourceType = MentionSourceApiTypesEnum.TOPIC;
        }

        return model;
    }


    override equals(other: AlertModel): boolean {
        if (!this.filterSearch?.equals(other?.filterSearch)) {
            return false;
        }

        return super.equals(other);
    }

    /**
     * Converts BriefingBlockApiModel[] to BlockModel[]
     */
    protected override mapBlocksFromApi(apiBlocks: BriefingBlockApiModel[] = [], columnWidth: number = 640): BlockModel[] {
        const blocks: BlockModel[] = [];

        apiBlocks.forEach(
            apiBlock => {
                let blockTypeEnum = briefingBlockTypeEnumFromApi.get(apiBlock.type);
                if (blockTypeEnum === BriefingBlockTypeEnum.LAYOUT) {
                    const columnNumber = (apiBlock as any).columns?.length;
                    blockTypeEnum = columnNumberToBriefingBlockTypeMap.get(columnNumber);
                }

                const blockType = alertBlockTypeEnumToBlockTypeMap.get(blockTypeEnum);

                if (!blockType) {
                    return;
                }

                let block;

                switch (apiBlock.type) {
                    case BriefingBlockTypeApiEnum.TITLE:
                        const titleApiData: BriefingTitleDisplayElementApiModel = apiBlock as BriefingTitleDisplayElementApiModel;
                        let titleApiBlockStyles = apiBlock.styles;

                        if (titleApiBlockStyles !== undefined) {
                            titleApiBlockStyles = JSON.parse(titleApiBlockStyles);
                            const styleState = this.getDefaultStyleState(titleApiBlockStyles);
                            this.setDefaultStyles(BriefingModel.defaultBriefingStyles[styleState].title, titleApiBlockStyles);
                        }

                        if (titleApiData?.title === 'Coverage Alert') {
                            if (this.alertType === AlertTypeEnum.SURGE) {
                                titleApiData.title = 'Coverage Surge Alert';
                            } else {
                                titleApiData.title = 'Recent Coverage Alert';
                            }
                        }

                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            new BriefingTitleDisplayElementModel(
                                titleApiData.title,
                                titleApiBlockStyles,
                                titleApiData.titleSettings,
                                titleApiData.automaticallyGenerated
                            )
                        );
                        break;

                    case BriefingBlockTypeApiEnum.TEXT:
                        const textApiData: BriefingTextDisplayElementApiModel = apiBlock as BriefingTextDisplayElementApiModel;
                        let textApiBlockStyles = apiBlock.styles;

                        if (textApiBlockStyles !== undefined) {
                            textApiBlockStyles = JSON.parse(textApiBlockStyles);
                            const styleState = this.getDefaultStyleState(textApiBlockStyles);
                            this.setDefaultStyles(BriefingModel.defaultBriefingStyles[styleState].text, textApiBlockStyles);
                        }

                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            new BriefingTextDisplayElementModel(textApiData.content, textApiBlockStyles)
                        );
                        (block.displayElement as BriefingTextDisplayElementModel).enableAIRefinement = true;

                        block.displayElement.htmlEdit = textApiBlockStyles['htmlEdit'];
                        break;

                    case BriefingBlockTypeApiEnum.IMAGE:
                        const imageApiData: BriefingImageDisplayElementApiModel = apiBlock as BriefingImageDisplayElementApiModel;

                        const SURGE_OLD_IMAGEURLS = [
                            'https://apigateway.agilitypr.com/api/v1/images/7dbec9a0-635c-4de8-9d5c-584e9bf50745',
                            'https://public-preprod.agilitypr.media/66890303-6d59-4590-adb1-0120fbec2205'
                        ];

                        const SURGE_NEW_IMAGEURL = 'https://public.agilitypr.media/be420990-10e3-492f-92ce-288b68875990';

                        if (this.alertType === AlertTypeEnum.SURGE && SURGE_OLD_IMAGEURLS.includes(imageApiData.imageProperties?.url)) {
                            imageApiData.imageProperties.url = SURGE_NEW_IMAGEURL;
                        }
                        if (this.alertType === AlertTypeEnum.SURGE && SURGE_OLD_IMAGEURLS.includes(imageApiData.images?.[0]?.url)) {
                            imageApiData.images[0].url = SURGE_NEW_IMAGEURL;
                        }

                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            new BriefingImageDisplayElementModel(imageApiData)
                        );

                        break;

                    case BriefingBlockTypeApiEnum.DATE:
                        const dateApiBlock = apiBlock as BriefingDateDisplayElementApiModel;
                        let dateApiBlockStyles = apiBlock.styles;

                        if (dateApiBlockStyles !== undefined) {
                            dateApiBlockStyles = JSON.parse(dateApiBlockStyles);
                            const styleState = this.getDefaultStyleState(dateApiBlockStyles);
                            this.setDefaultStyles(BriefingModel.defaultBriefingStyles[styleState].date, dateApiBlockStyles);
                        }

                        // if the date doesn't exist, use the default (current date)
                        let timestamp;
                        if (dateApiBlock.timestamp) {
                            // The api uses UTC time, so we must convert it to the local time to be used properly.
                            timestamp = +new Date(dateApiBlock.timestamp + (new Date().getTimezoneOffset() * 1000 * 60));
                        }
                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            new DateDisplayElementModel(
                                timestamp,
                                dateApiBlock.format,
                                dateApiBlockStyles
                            )
                        );

                        break;

                    case BriefingBlockTypeApiEnum.ALERT_MENTIONS:
                        const mentionApiBlock = apiBlock as AlertMentionListDisplayElementApiModel;
                        let mentionApiBlockStyles = apiBlock.styles;

                        if (mentionApiBlockStyles !== undefined) {
                            mentionApiBlockStyles = JSON.parse(mentionApiBlockStyles);
                            const styleState = this.getDefaultStyleState(mentionApiBlockStyles);
                            this.setDefaultStyles(BriefingModel.defaultBriefingStyles[styleState].mention, mentionApiBlockStyles);
                        }

                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            new AlertMentionListDisplayElementModel(
                                mentionApiBlock.title,
                                null,
                                mentionApiBlock.searchCriteriaId,
                                mentionApiBlock.maxNumberOfMentionsInEmail ? mentionApiBlock.maxNumberOfMentionsInEmail : 10,
                                mentionApiBlock.filterSearchCriteriaId,
                                undefined,
                                mentionApiBlockStyles
                            )
                        );
                        block.canAddOrRemove = false;
                        break;

                    case BriefingBlockTypeApiEnum.SURGE_ALERT:
                        const surgeApiBlock = apiBlock as AlertSurgeDisplayElementApiModel;

                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            new AlertSurgeDisplayElementModel(
                                surgeApiBlock
                            )
                        );
                        block.canAddOrRemove = false;
                        block.canDuplicate = false;
                        block.canEdit = false;

                        break;
                    case BriefingBlockTypeApiEnum.AUTOMATIC_MENTION_LIST:
                        const automatedApiBlock = apiBlock as BriefingAutomaticMentionListDisplayElementApiModel;
                        let automatedApiBlockStyles = apiBlock.styles;

                        if (automatedApiBlockStyles !== undefined) {
                            automatedApiBlockStyles = JSON.parse(automatedApiBlockStyles);
                            const styleState = this.getDefaultStyleState(automatedApiBlockStyles);
                            this.setDefaultStyles(BriefingModel.defaultBriefingStyles[styleState].mention, automatedApiBlockStyles);
                        }

                        let sourceType: MonitoringSeriesTypeEnum = MonitoringSeriesTypeEnum.topic;


                        sourceType = mentionSourceApiTypesEnumToMonitoringSeriesTypeEnum.get(automatedApiBlock.sourceType);


                        const dynamicMentionListDisplayElementModel = new DynamicMentionListDisplayElementModel(
                            automatedApiBlock.title,
                            sourceType,
                            automatedApiBlock.searchCriteriaId,
                            automatedApiBlock.maxNumberOfMentionsInEmail ? automatedApiBlock.maxNumberOfMentionsInEmail : 10,
                            automatedApiBlock.filterSearchCriteriaId,
                            undefined,
                            automatedApiBlockStyles,
                            automatedApiBlock.blockReferenceId,
                            automatedApiBlock.mentionPreviewCount,
                            automatedApiBlock.automaticallyGenerated
                        );

                        dynamicMentionListDisplayElementModel.useSearchDateFilter = automatedApiBlock.useSearchDateFilter;

                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            dynamicMentionListDisplayElementModel
                        );
                        break;
                    case BriefingBlockTypeApiEnum.ANALYSIS:
                        const analysisApiBlock = apiBlock as BriefingAnalysisDisplayElementApiModel;
                        let analysisApiBlockStyles = apiBlock.styles;

                        if (analysisApiBlockStyles !== undefined) {
                            analysisApiBlockStyles = JSON.parse(analysisApiBlockStyles);
                            const styleState = this.getDefaultStyleState(analysisApiBlockStyles);
                            this.setDefaultStyles(BriefingModel.defaultBriefingStyles[styleState].analysis, analysisApiBlockStyles);
                        }

                        const analysisDisplayElementModel = new AnalysisDisplayElementModel(
                            analysisApiBlock.value,
                            analysisApiBlockStyles,
                            analysisApiBlock
                        );
                        if (analysisDisplayElementModel.useSearchDateFilter === undefined) {
                            analysisDisplayElementModel.useSearchDateFilter = false;
                        }
                        analysisDisplayElementModel.enableAIRefinement = true;

                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            analysisDisplayElementModel
                        );
                        break;
                    case BriefingBlockTypeApiEnum.COVERAGE_CURATOR:
                        const curatorApiBlock = apiBlock as BriefingCoverageCuratorDisplayElementApiModel;
                        let curatorApiBlockStyles = apiBlock.styles;

                        if (curatorApiBlockStyles !== undefined) {
                            curatorApiBlockStyles = JSON.parse(curatorApiBlockStyles);
                            const styleState = this.getDefaultStyleState(curatorApiBlockStyles);
                            this.setDefaultStyles(BriefingModel.defaultBriefingStyles[styleState].coverageCurator, curatorApiBlockStyles);
                        }

                        const curatorDisplayElementModel = new CuratorDisplayElement(
                            curatorApiBlockStyles,
                            curatorApiBlock
                        );
                        if (curatorDisplayElementModel.useSearchDateFilter === undefined) {
                            curatorDisplayElementModel.useSearchDateFilter = false;
                        }

                        const innerBlocks = this.mapBlocksFromApi(curatorApiBlock.blocks);
                        const originalBlocks = curatorApiBlock.originalBlocks ? this.mapBlocksFromApi(JSON.parse(curatorApiBlock.originalBlocks)) : [];
                        [...innerBlocks, ...originalBlocks].forEach(x => {
                            x.displayElement.canAutomaticallyGenerated = true;
                            (x.displayElement as any).useSearchDateFilter = curatorDisplayElementModel.useSearchDateFilter;
                        });
                        block = new CuratorBlock(
                            apiBlock.id,
                            blockType as CuratorBlockType,
                            curatorDisplayElementModel,
                            innerBlocks || [],
                            originalBlocks
                        );
                        break;

                    case BriefingBlockTypeApiEnum.LAYOUT:
                        const layoutDisplayElement = new LayoutDisplayElement(apiBlock.styles);
                        const numColumnsFromApi = apiBlock.columns.length;
                        this.recalculateLayoutWidth(layoutDisplayElement.styles, numColumnsFromApi);
                        let paddingWidth = (layoutDisplayElement.styles.columnSpacing || 0) * Math.max(0, numColumnsFromApi - 1);
                        if (layoutDisplayElement.styles.padding?.paddingOption) {
                            paddingWidth = paddingWidth + (layoutDisplayElement.styles.padding.leftPadding || 0)
                                                        + (layoutDisplayElement.styles.padding.rightPadding || 0);
                        } else if (layoutDisplayElement.styles.padding?.paddingOption === false) {
                            paddingWidth += (layoutDisplayElement.styles.padding.paddingAll * 2 || 0);
                        }
                        block = new LayoutBlock(
                            null,
                            blockType as LayoutBlockType,
                            layoutDisplayElement,
                            apiBlock.columns.map((column, index) => {
                                return this.mapBlocksFromApi(
                                    column.blocks,
                                    (columnWidth - paddingWidth) * parseFloat(layoutDisplayElement.styles.width?.[index]) / 100
                                );
                            })
                        );
                        break;

                    case BriefingBlockTypeApiEnum.CHART:
                        const chartApiBlock = apiBlock as BriefingChartDisplayElementApiModel;
                        const chartApiBlockStyles = apiBlock.styles;

                        if (chartApiBlockStyles !== undefined) {
                            const chartApiBlockStylesModel = JSON.parse(chartApiBlockStyles);

                            const styleState = this.getDefaultStyleState(chartApiBlockStylesModel);
                            this.setDefaultStyles(BriefingModel.defaultBriefingStyles[styleState].image, chartApiBlockStylesModel);
                        }

                        const chartDisplayElement = new BriefingChartDisplayElementModel(
                            chartApiBlock
                        );
                        let chartPaddingWidth = 0;
                        if (chartDisplayElement.styles.padding?.paddingOption) {
                            chartPaddingWidth = chartPaddingWidth + (chartDisplayElement.styles.padding.leftPadding || 0)
                                                        + (chartDisplayElement.styles.padding.rightPadding || 0);
                        } else if (chartDisplayElement.styles.padding?.paddingOption === false) {
                            chartPaddingWidth += (chartDisplayElement.styles.padding.paddingAll * 2 || 0);
                        }
                        if (chartDisplayElement.styles.doBorder) {
                            chartPaddingWidth += (chartDisplayElement.styles.borderWidth || 0) * 2;
                        }
                        // eslint-disable-next-line max-len
                        chartDisplayElement.chart.widthPercent = Math.min(100, Math.round(chartDisplayElement.chart.widthPx / (columnWidth - chartPaddingWidth) * 100));
                        if (chartApiBlock['mentionSearches']?.length) {
                            chartDisplayElement.mentionSearches = chartApiBlock['mentionSearches'];
                        }

                        block = new BlockModel(
                            apiBlock.id,
                            blockType,
                            chartDisplayElement
                        );

                        break;
                    case BriefingBlockTypeApiEnum.DIVIDER:
                        const dividerApiData = apiBlock as BriefingAutomaticMentionListDisplayElementApiModel;
                        block = new BlockModel(
                            null,
                            blockType,
                            new DividerDisplayElementModel(dividerApiData.styles)
                        );
                        break;
                    default:
                        // Unknown block type, do nothing
                        break;
                }

                // we removed isBoundToGlobalStyles checkbox for mentions.. so we always want it to be true
                if (apiBlock.styles && (apiBlock.type !== BriefingBlockTypeApiEnum.ALERT_MENTIONS)) {
                    // backend sometimes passes "null"
                    const styles = JSON.parse(apiBlock.styles);
                    if (styles) {
                        // @ TODO - NEP-3392 hack should add isBoundToGlobalStyles to
                        block.displayElement.isBoundToGlobalStyles = styles.isBoundToGlobalStyles === true || styles.isBoundToGlobalStyles === 'true';
                    } else {
                        block.displayElement.isBoundToGlobalStyles = true;
                    }
                } else {
                    block.displayElement.isBoundToGlobalStyles = true;
                }
                blocks.push(block);
            }
        );

        return blocks;
    }

    /**
     * Converts BlockModel[] to BriefingBlockApiModel[]
     */
    protected override mapBlocksToApi(blocks: BlockModel[], columnWidth: number = 640, forClone: boolean = false): BriefingBlockApiModel[] {
        const apiBlocks: BriefingBlockApiModel[] = [];

        blocks.forEach(
            block => {
                if (!block.type || !block.type.key) {
                    return;
                }

                // hack @TODO NEP-3392
                let styles;
                // endhack

                switch (block.type.key) {
                    case BriefingBlockTypeEnum.TITLE:
                        const titleDisplayElement = block.displayElement as BriefingTitleDisplayElementModel;

                        // hack @TODO NEP-3392
                        styles = titleDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = titleDisplayElement.isBoundToGlobalStyles;
                        // endhack

                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            title: titleDisplayElement.text,
                            automaticallyGenerated: titleDisplayElement.automaticallyGenerated,
                            titleSettings: JSON.stringify({ headingLevel: titleDisplayElement.headingLevel }),
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                        } as BriefingTitleDisplayElementApiModel);

                        break;

                    case BriefingBlockTypeEnum.TEXT:
                        const textDisplayElement = block.displayElement as TextDisplayElementModel;

                        // hack @TODO NEP-3392
                        styles = textDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = textDisplayElement.isBoundToGlobalStyles;
                        styles.htmlEdit = textDisplayElement.htmlEdit;
                        // endhack

                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            content: textDisplayElement.text,
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                        } as BriefingTextDisplayElementApiModel);

                        break;

                    case BriefingBlockTypeEnum.IMAGE:
                        const imageDisplayElement = block.displayElement as BriefingImageDisplayElementModel;

                        const apiBlock = imageDisplayElement.toApi();

                        // hack @TODO NEP-3392
                        styles = imageDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = imageDisplayElement.isBoundToGlobalStyles;
                        styles.originalWidth = imageDisplayElement.images?.[0]?.width;

                        apiBlock.styles = JSON.stringify(styles);
                        apiBlock.id = block.id;
                        apiBlock.customStyleId = styles && Number.isInteger(styles.styleState) ? styles.styleState : null;
                        apiBlock.type = briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum);

                        apiBlocks.push(apiBlock);
                        break;

                    case BriefingBlockTypeEnum.DATE:
                        const dateDisplayElement = block.displayElement as DateDisplayElementModel;

                        // Default to 0 because the api uses 0 to mean "send date".
                        let timestamp: number = 0;
                        if (dateDisplayElement.date) {
                            // Get the timestamp from the date object and convert it to UTC timezone.
                            timestamp = dateDisplayElement.date - (new Date().getTimezoneOffset() * 1000 * 60);
                        }

                        // hack @TODO NEP-3392
                        styles = dateDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = dateDisplayElement.isBoundToGlobalStyles;
                        // endhack

                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            timestamp: timestamp,
                            format: dateDisplayElement.format,
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                            date: styles ? styles.date : null
                        } as BriefingDateDisplayElementApiModel);

                        break;

                    case BriefingBlockTypeEnum.ALERT_MENTIONS:
                        const alertDisplayElement = block.displayElement as AlertMentionListDisplayElementModel;

                        // hack @TODO NEP-3392
                        styles = alertDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = alertDisplayElement.isBoundToGlobalStyles;
                        // endhack

                        // TODO: map sort and page size, and add search criteria later
                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            title: alertDisplayElement.title,
                            filterSearchCriteriaId: alertDisplayElement.searchCriteriaId,
                            searchCriteriaId: alertDisplayElement.backendDataSource.id,
                            sortField: SearchCriteriaSortFieldEnum.published,
                            sortOrder: SortOrderEnum.descending,
                            maxNumberOfMentionsInEmail: alertDisplayElement.pageSize,
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                        } as AlertMentionListDisplayElementApiModel);

                        break;

                    case BriefingBlockTypeEnum.SURGE_ALERT:
                        const surgeDisplayElement = block.displayElement as AlertSurgeDisplayElementModel;

                        styles = surgeDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = surgeDisplayElement.isBoundToGlobalStyles;

                        // TODO: map sort and page size, and add search criteria later
                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            metric: surgeDisplayElement.metric,
                            sensitivity: surgeDisplayElement.sensitivity,
                            coolDownPeriod: surgeDisplayElement.coolDownPeriod,
                        } as unknown as AlertSurgeDisplayElementApiModel);

                        break;
                    case BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST:
                        const dynamicDisplayElement = block.displayElement as DynamicMentionListDisplayElementModel;

                        // hack @TODO NEP-3392
                        styles = dynamicDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = dynamicDisplayElement.isBoundToGlobalStyles;
                        // endhack

                        // TODO: map sort and page size, and add search criteria later
                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            title: dynamicDisplayElement.title,
                            filterSearchCriteriaId: dynamicDisplayElement.searchCriteriaId,
                            searchCriteriaId: dynamicDisplayElement.backendDataSource.id,
                            maxNumberOfMentionsInEmail: dynamicDisplayElement.pageSize,
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                            sourceType: dynamicDisplayElement.sourceType,
                            briefingAutoGenerateInsightRequest: dynamicDisplayElement.additionalSettings.autoGenInsight,
                            keywords: dynamicDisplayElement.additionalSettings.precisionClip?.manualKeywords,
                            generateInsights: dynamicDisplayElement.additionalSettings.precisionClip?.generateInsights,
                            extendedStart: dynamicDisplayElement.additionalSettings.precisionClip?.extendedStart,
                            extendedEnd: dynamicDisplayElement.additionalSettings.precisionClip?.extendedEnd,
                            blockReferenceId: dynamicDisplayElement.blockReferenceId,
                            mentionPreviewCount: dynamicDisplayElement.mentionPreviewCount,
                            automaticallyGenerated: dynamicDisplayElement.automaticallyGenerated,
                            useSearchDateFilter: dynamicDisplayElement.useSearchDateFilter
                        } as unknown as BriefingAutomaticMentionListDisplayElementApiModel);

                        break;
                    case BriefingBlockTypeEnum.ANALYSIS:
                        const analysisDisplayElement = block.displayElement as AnalysisDisplayElementModel;

                        const analysisBlock = analysisDisplayElement.toApiModel();
                        analysisBlock.id = block.id;
                        analysisBlock.type = briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum);

                        apiBlocks.push(analysisBlock);
                        break;
                    case BriefingBlockTypeEnum.COVERAGE_CURATOR:

                        const curatorBlock = block as CuratorBlock;
                        const curatorDisplayElement = curatorBlock.displayElement;
                        styles = curatorDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = curatorDisplayElement.isBoundToGlobalStyles;

                        const originalBlocks = curatorBlock.originalBlocks?.length ?
                            JSON.stringify(this.mapBlocksToApi(curatorBlock.originalBlocks)) : null;
                        const bulkAnalysisMessage = curatorDisplayElement.bulkAnalysisMessage?.toApiModel()?.bulkAnalysisMessage;
                        if (bulkAnalysisMessage?.aiAnalysisType !== AiAnalysisTypes.COLLECTION_KEY_MESSAGES_DEFAULT) {
                            bulkAnalysisMessage.numberMentions = 20;
                            curatorBlock.blocks.forEach(item => {
                                if (item.type.key === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST && item.displayElement.automaticallyGenerated) {
                                    bulkAnalysisMessage.numberMentions = (item.displayElement as DynamicMentionListDisplayElementModel).pageSize;
                                }
                            });
                        }
                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            filterSearchCriteriaId: curatorDisplayElement.searchCriteriaId,
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                            blocks: this.mapBlocksToApi((block as CuratorBlock).blocks),
                            searches: [{
                                searchCriteriaId: curatorDisplayElement.backendDataSource?.id,
                                filterSearchCriteriaId: curatorDisplayElement.searchCriteriaId,
                                sourceType: monitoringSeriesTypeEnumToMentionSourceApiTypesEnum.get(curatorDisplayElement.backendDataSource?.type)
                            } as BriefingSearchApi],
                            generateOnSend: curatorDisplayElement.generateOnSend,
                            originalBlocks,
                            bulkAnalysisMessage,
                            useSearchDateFilter: curatorDisplayElement.useSearchDateFilter
                        } as unknown as BriefingCoverageCuratorDisplayElementApiModel);
                        break;

                    case BriefingBlockTypeEnum.CHART:
                        const briefingChartDisplayElement = block.displayElement as BriefingChartDisplayElementModel;
                        const reportChartDisplayElement = briefingChartDisplayElement.chartBlock?.displayElement as ChartDisplayElementModel;
                        styles = briefingChartDisplayElement.styles.toBlockStyle();
                        styles.accentColor = briefingChartDisplayElement.styles.accentColor;
                        styles.isBoundToGlobalStyles = briefingChartDisplayElement.isBoundToGlobalStyles;
                        styles.widthPercent = briefingChartDisplayElement.chart.widthPercent;
                        if (
                            briefingChartDisplayElement.chart.generateOnSend &&
                            briefingChartDisplayElement?.chart?.chartUrl
                        ) {
                            delete briefingChartDisplayElement.chart.chartUrl;
                        }

                        const chartDisplayElement = briefingChartDisplayElement?.chartBlock?.displayElement as ChartDisplayElementModel;
                        chartDisplayElement?.series?.forEach(series => series.cacheResult = !briefingChartDisplayElement.chart.generateOnSend);

                        const chartApiBlock = {
                            id: block.id,
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            chartUrl: briefingChartDisplayElement.chart.chartUrl,
                            generateOnSend: briefingChartDisplayElement.chart.generateOnSend,
                            chartOptions: JSON.stringify(briefingChartDisplayElement.toChartOptionsApi()),
                            showPreviousPeriod: reportChartDisplayElement?.chartHeaderSettings?.previousPeriod,
                            searches: briefingChartDisplayElement.toSearchesApi(),
                            imageProperties: {
                                url: briefingChartDisplayElement.url ? briefingChartDisplayElement.url : '',
                                width: briefingChartDisplayElement.chart.widthPx,
                                caption: briefingChartDisplayElement.settings?.caption,
                                altText: briefingChartDisplayElement.settings?.altText,
                                displaySize: briefingChartDisplayElement.chart?.heightPx,
                            },
                            sources: briefingChartDisplayElement.toSourcesApi(),
                            useSearchDateFilter: briefingChartDisplayElement.useSearchDateFilter
                        };
                        if (forClone) {
                            chartApiBlock['mentionSearches'] = briefingChartDisplayElement.mentionSearches;
                        }

                        apiBlocks.push(chartApiBlock);
                        break;
                    case BriefingBlockTypeEnum.DIVIDER:
                        const dividerDisplayElement = block.displayElement as DividerDisplayElementModel;

                        // hack @TODO NEP-3392
                        styles = dividerDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = dividerDisplayElement.isBoundToGlobalStyles;
                        // endhack

                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                        } as BriefingDividerDisplayElementApiModel);

                        break;
                    case BriefingBlockTypeEnum.ONE_COLUMN_LAYOUT:
                    case BriefingBlockTypeEnum.TWO_COLUMN_LAYOUT:
                    case BriefingBlockTypeEnum.THREE_COLUMN_LAYOUT:
                        const layoutDisplayElement = block.displayElement as LayoutDisplayElement;
                        styles = layoutDisplayElement.styles.toBlockStyle();
                        styles.isBoundToGlobalStyles = layoutDisplayElement.isBoundToGlobalStyles;
                        const numColumnsToApi = (block as LayoutBlock).blocks.length;
                        this.recalculateLayoutWidth(styles, numColumnsToApi);
                        let paddingWidth = (layoutDisplayElement.styles.columnSpacing || 0) * Math.max(0, numColumnsToApi - 1);
                        if (layoutDisplayElement.styles.padding?.paddingOption) {
                            paddingWidth = paddingWidth + (layoutDisplayElement.styles.padding.leftPadding || 0)
                                + (layoutDisplayElement.styles.padding.rightPadding || 0);
                        } else if (layoutDisplayElement.styles.padding?.paddingOption === false) {
                            paddingWidth += (layoutDisplayElement.styles.padding.paddingAll * 2 || 0);
                        }

                        apiBlocks.push({
                            id: block.id,
                            type: briefingBlockTypeEnumToApi.get(block.type.key as BriefingBlockTypeEnum),
                            styles: JSON.stringify(styles),
                            columns: (block as LayoutBlock).blocks.map((column, index) => {
                                return {
                                    type: 'COLUMN',
                                    blocks: this.mapBlocksToApi(column, (columnWidth - paddingWidth) * parseFloat(styles.width?.[index]) / 100)
                                };
                            }),
                            customStyleId: styles && Number.isInteger(styles.styleState) ? styles.styleState : null
                        });
                        break;
                    default:
                        // Unknown block type, do nothing
                        break;
                }
            }
        );

        apiBlocks.forEach(
            block => {
                if (!block.id) {
                    delete block.id;
                }
            }
        );

        return apiBlocks;
    }

    /**
     * Returns a copy of this model reset as if it were not yet saved
     */
    public override clone(): AlertModel {
        const clone = new AlertModel(this.toApiModel());
        delete clone.id;
        clone.status = BriefingStatusEnum.DRAFT;
        clone.createdByUserId = null;
        clone.createdDate = null;
        clone.modifiedByUserId = null;
        clone.modifiedDate = null;
        clone.name = null;

        return clone;
    }
}
